// Câblage de production de l'exécuteur des prises (spec anti-perte phase 1,
// §5.2) : les dépendances réelles de `runTake` (Supabase, Storage, natif),
// UNE instance du signaleur automatique et UN magasin des choix faits pour
// les prises (projet, capture à compléter) pour toute l'app. L'écran de capture
// (A8) et le filet global (A9) passent par ici : s'ils avaient chacun leur
// signaleur, le dédoublonnage par prise (file série + magasin) ne tiendrait
// plus et la même prise pourrait être signalée deux fois.
import { CapacitorAudioRecorder } from "@capgo/capacitor-audio-recorder";
import type { SupabaseClient } from "@supabase/supabase-js";
import { createTakeRecorder, type TakeRecorder } from "../native/take-recorder";
import { envoyerSignalement } from "../signalement/envoyer";
import { createAutoSignaleur } from "./auto-signalement";
import { capacitorPreferencesAdapter } from "./preferences-adapter";
import { defaultResumableUpload } from "./resumable-upload";
import { forgetMissingSegments, handOffToServer } from "./segment-upload";
import { createCaptureStorage } from "./storage";
import { syncCaptureToServer } from "./sync-capture";
import {
  createCaptureAudioOverwriter,
  createCompletingConfirmer,
  createCompletingHandOff,
  takeCompletesExistingCapture,
} from "./take-completion";
import {
  createTakeOverrideStore,
  type TakeOverrideStore,
  withTakeOverrides,
} from "./take-overrides";
import {
  createServerAudioProbe,
  createServerCaptureFetcher,
  createTakeCaptureCreator,
  createUploadConfirmer,
  type RunnerDeps,
} from "./take-runner";
import { defaultReadAudio } from "./upload";

let signaleur: RunnerDeps["signaler"] | null = null;
let overrides: TakeOverrideStore | null = null;

/**
 * Émis quand un écran veut que l'exécuteur passe TOUT DE SUITE sur les prises
 * (projet choisi, enregistrement retrouvé, restauration depuis la corbeille) :
 * le filet global (`use-resync-pending`) l'écoute et lance un passage dans son
 * verrou, avec ses gardes (session, jamais sur l'écran de capture).
 */
export const REQUEST_TAKES_RUN_EVENT = "captureBesoins.requestTakesRun";

export function requestTakesRun(): void {
  window.dispatchEvent(new Event(REQUEST_TAKES_RUN_EVENT));
}

/**
 * « Réessayer » (accueil, fiche) : le serveur n'a pas certains segments
 * (S-m3). La prise de cette capture les retire de ses envoyés et repasse en
 * envoi ; l'appelant relance ensuite l'exécuteur. Best-effort : ne lève jamais.
 */
export async function forgetMissingSegmentsOfCapture(
  captureId: string,
  missing: string[],
): Promise<void> {
  try {
    const recorder = withTakeOverrides(createTakeRecorder(), sharedTakeOverrides());
    const { takes } = await recorder.listTakes();
    const take = takes.find((t) => t.captureId === captureId && t.active !== true);
    if (take) await forgetMissingSegments(recorder, take.takeId, missing);
  } catch (err) {
    console.warn(`[take-runtime] segments manquants de ${captureId} non repris`, err);
  }
}

/** Les choix faits pour les prises (projet, capture à compléter), magasin persistant unique. */
export function sharedTakeOverrides(): TakeOverrideStore {
  overrides ??= createTakeOverrideStore(capacitorPreferencesAdapter);
  return overrides;
}

/** Le signaleur automatique de l'app (magasin persistant, Capacitor Preferences). */
export function sharedAutoSignaleur(): RunnerDeps["signaler"] {
  signaleur ??= createAutoSignaleur({
    kv: capacitorPreferencesAdapter,
    envoyer: envoyerSignalement,
    diagnostics: () => CapacitorAudioRecorder.getDiagnostics(),
  });
  return signaleur;
}

export interface ProductionRunnerInput {
  client: SupabaseClient;
  user: { id: string; email: string | null | undefined };
  recorder: TakeRecorder;
  offerResume: RunnerDeps["offerResume"];
}

export function createProductionRunnerDeps(o: ProductionRunnerInput): RunnerDeps {
  const isOnline = () => navigator.onLine;
  const choices = sharedTakeOverrides();
  // Chaque manifeste lu par l'exécuteur porte les choix de la personne
  // (projet, capture à compléter) : le natif ne réécrit jamais les métadonnées.
  const recorder = withTakeOverrides(o.recorder, choices);
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  return {
    recorder,
    fetchServerCapture: createServerCaptureFetcher(o.client),
    serverAudioAbsent: createServerAudioProbe(o.client),
    createCapture: createTakeCaptureCreator({
      storage: createCaptureStorage(capacitorPreferencesAdapter),
      sync: (capture) =>
        syncCaptureToServer({
          client: o.client,
          capture,
          user: { id: o.user.id, email: o.user.email },
          supabaseUrl,
          anonKey,
        }),
    }),
    // C25 : une prise qui complète une capture écrase son audio avant d'être confirmée.
    confirmUploaded: createCompletingConfirmer({
      confirm: createUploadConfirmer({ client: o.client, recorder }),
      overwriter: createCaptureAudioOverwriter({
        client: o.client,
        recorder,
        // Résolus à l'appel, pas à la construction (comme le dernier recours) :
        // les écrans qui construisent ces dépendances sans jamais envoyer
        // d'audio n'en dépendent pas.
        readFile: (uri) => defaultReadAudio(uri),
        resumableUpload: (p) => defaultResumableUpload(p),
        supabaseUrl,
        anonKey,
      }),
      overrides: choices,
    }),
    handOff: createCompletingHandOff({
      handOff: (m, meta, opts) =>
        handOffToServer(
          { client: o.client, recorder, readFile: defaultReadAudio, isOnline },
          m,
          meta,
          opts,
        ),
      overrides: choices,
    }),
    signaler: sharedAutoSignaleur(),
    isOnline,
    sleep: (ms) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
    offerResume: o.offerResume,
    completesExistingCapture: (m) => takeCompletesExistingCapture(choices, m),
  };
}
